import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  BRAND_COLORS,
  DIFFICULTY_MODES,
  DIFFICULTY_MODE_SPECS,
  MODE_ASSETS,
  type DifficultyMode,
  type MeResponse,
  type SessionResponse,
} from '@lk/shared';
import { BrandImg } from '../../components/BrandImg';
import { Button } from '../../components/Button';
import type { IconName } from '../../components/Icon';
import { Panel } from '../../components/Panel';
import { Pop, TapScale } from '../../components/Motion';
import { TopBar } from '../../components/TopBar';
import { ErrorState } from '../../components/States';
import { cn } from '../../lib/cn';
import { coinsLabel, minutesLabel, questionsLabel } from '../../lib/format';
import { newIdempotencyKey } from '../../lib/ids';
import { breakResumeAt } from '../../lib/errors';
import { startSession } from '../../lib/api';
import { playWindowQuery } from '../learner/queries';
import { saveSession } from '../../offline/sessions';
import { useUiStore } from '../../store/uiStore';
import { playSound } from '../../audio/sound';
import { estimateMode, expectedCoins } from './estimate';

const LENGTHS = [10, 15, 20] as const;

const MODE_VISUALS: Record<DifficultyMode, { icon: IconName; colors: readonly [string, string] }> = {
  training: { icon: 'mode-training', colors: [BRAND_COLORS.green, '#B6FF5C'] },
  showdown: { icon: 'mode-showdown', colors: [BRAND_COLORS.cyan, BRAND_COLORS.magenta] },
  challenge: { icon: 'mode-challenge', colors: [BRAND_COLORS.red, BRAND_COLORS.goldDeep] },
};

export function ModeSelectScreen({
  me,
  initialMode,
}: {
  me: MeResponse;
  initialMode?: DifficultyMode;
}): React.JSX.Element {
  const navigate = useNavigate();
  const learnerId = me.user.learnerId ?? me.learners[0]?.id ?? '';
  const lastMode = useUiStore((s) => s.lastMode);
  const lastLength = useUiStore((s) => s.lastLength);
  const setLastMode = useUiStore((s) => s.setLastMode);
  const setLastLength = useUiStore((s) => s.setLastLength);

  const [mode, setMode] = useState<DifficultyMode>(initialMode ?? lastMode);
  const [length, setLength] = useState<number>(lastLength);

  /**
   * Ключ ідемпотентності живе стільки ж, скільки намір «почати цю сесію».
   * Якщо перший запит обірвався і дитина тисне ще раз — сервер поверне
   * ту саму сесію, а не створить другу.
   */
  const idempotencyKey = useRef(newIdempotencyKey());

  // Вікно гри перепитуємо при кожному відкритті екрана: рішення «пускати чи
  // ні» приймається просто зараз, а кеш запитів переживає і ніч, і перезапуск.
  const playWindow = useQuery({
    ...playWindowQuery(learnerId),
    enabled: Boolean(learnerId),
    refetchOnMount: 'always',
  });
  const breakUntil = playWindow.data?.breakUntil ?? null;
  useEffect(() => {
    if (!breakUntil) return;
    if (new Date(breakUntil).getTime() <= Date.now()) return;
    void navigate({ to: '/break' });
  }, [breakUntil, navigate]);

  /**
   * Скільки лишилось до перерви. Кажемо це ДО старту: сервер підріже сесію
   * під цей залишок, і дитина має розуміти, чому питань менше, ніж обрала.
   */
  const playUntil = playWindow.data?.playUntil ?? null;

  // Годинник тікає сам: запит за вікном оновлюється раз на пів хвилини, а
  // межа відрізка настає між оновленнями — і кнопку треба замкнути на ній,
  // а не тоді, коли серверу заманеться відповісти.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!playUntil) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [playUntil]);

  // Округлюємо ВГОРУ: «0 хв» має означати рівно «часу вже немає», а не
  // «лишилось сорок секунд». Інакше напис суперечив би замку на кнопці.
  const minutesToBreak = playUntil
    ? Math.max(0, Math.ceil((new Date(playUntil).getTime() - now) / 60_000))
    : null;
  /*
   * Часу не лишилось. Сервер у цьому стані все одно видасть сесію — але
   * мінімальну, на 5 питань, які дитина не встигне побачити: кожен тап
   * піднімав би лічильник показів і спалював банк у 30-денний кулдаун.
   * Тому «У бій» замикаємо ми.
   */
  const outOfTime = minutesToBreak === 0;
  const breakMinutes = playWindow.data?.breakMinutes ?? 0;

  const start = useMutation<SessionResponse>({
    mutationFn: () =>
      startSession({
        learnerId,
        difficultyMode: mode,
        length,
        mode: 'daily',
        idempotencyKey: idempotencyKey.current,
      }),
    onError: (error) => {
      // Сервер міг закрити відрізок гри рівно між відкриттям екрана
      // й натисканням кнопки. Тоді це не помилка, а перерва — і показати
      // треба відлік, а не червону панель.
      if (breakResumeAt(error)) void navigate({ to: '/break' });
    },
    onSuccess: async (session) => {
      // Сесія одразу лягає в IndexedDB — далі її можна догравати навіть офлайн.
      await saveSession(session, learnerId);
      setLastMode(mode);
      setLastLength(length);
      playSound('sessionStart');
      idempotencyKey.current = newIdempotencyKey();
      void navigate({ to: '/play/$sessionId', params: { sessionId: session.sessionId } });
    },
  });

  return (
    <main className="lk-shell lk-safe flex min-h-dvh flex-col gap-4 pb-8">
      <TopBar title="Обери бій" back={{ to: '/arena' }} />

      <section aria-label="Режими складності" className="flex flex-col gap-3">
        {DIFFICULTY_MODES.map((id, cardIndex) => {
          const spec = DIFFICULTY_MODE_SPECS[id];
          const visual = MODE_VISUALS[id];
          const estimate = estimateMode(id, length);
          const selected = id === mode;
          return (
            <Pop key={id} id={id} delay={cardIndex * 0.06}>
            <TapScale
              onClick={() => {
                playSound('tap');
                setMode(id);
              }}
              aria-pressed={selected}
              data-testid={`mode-${id}`}
              className={cn(
                'lk-panel flex w-full items-stretch gap-3 p-3 text-left',
                selected && 'ring-2 ring-[var(--lk-gold)]',
              )}
              style={selected ? { borderColor: BRAND_COLORS.gold } : undefined}
            >
              <BrandImg
                src={MODE_ASSETS[id].path}
                alt={spec.title}
                fallbackIcon={visual.icon}
                colors={visual.colors}
                shape="rounded"
                className="h-24 w-24 shrink-0"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-black">{spec.title}</h2>
                  {selected ? <span className="lk-chip text-[10px]">обрано</span> : null}
                </div>
                <p className="mt-0.5 text-xs font-bold text-white/60">{spec.subtitle}</p>

                <dl className="mt-2 grid grid-cols-3 gap-1.5 text-[11px] font-black">
                  <div className="lk-panel-tight px-2 py-1.5">
                    <dt className="text-white/45">до</dt>
                    <dd className="tabular text-[var(--lk-gold)]">{estimate.maxCoins} грн</dd>
                  </div>
                  <div className="lk-panel-tight px-2 py-1.5">
                    <dt className="text-white/45">ризик</dt>
                    <dd
                      style={{
                        color:
                          estimate.riskLabel === 'низький'
                            ? BRAND_COLORS.green
                            : estimate.riskLabel === 'середній'
                              ? BRAND_COLORS.gold
                              : BRAND_COLORS.red,
                      }}
                    >
                      {estimate.riskLabel}
                    </dd>
                  </div>
                  <div className="lk-panel-tight px-2 py-1.5">
                    <dt className="text-white/45">бонус</dt>
                    <dd className="tabular text-[var(--lk-cyan)]">
                      {spec.completionBonus > 0 ? `+${spec.completionBonus}` : '—'}
                    </dd>
                  </div>
                </dl>

                <p className="mt-1.5 text-[11px] font-bold text-white/45">
                  реально очікувати ≈ {coinsLabel(expectedCoins(id, length))} ·{' '}
                  {estimate.hardCount > 0
                    ? `${estimate.hardCount} складних по 2 бали`
                    : 'усі по 1 балу'}
                </p>
              </div>
            </TapScale>
            </Pop>
          );
        })}
      </section>

      <Panel tight className="p-4">
        <p className="text-[11px] font-black tracking-widest text-white/50 uppercase">
          Скільки питань
        </p>
        <div className="mt-2 grid grid-cols-3 gap-2">
          {LENGTHS.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => {
                playSound('tap');
                setLength(value);
              }}
              aria-pressed={length === value}
              data-testid={`length-${value}`}
              className={cn(
                'lk-btn h-14 text-xl',
                length === value && 'lk-btn--gold',
              )}
            >
              {value}
            </button>
          ))}
        </div>
        <p className="mt-2 text-[11px] font-bold text-white/45">
          {questionsLabel(length)} · орієнтовно {Math.max(3, Math.round((length * 40) / 60))} хв
        </p>
        {outOfTime ? (
          <p
            className="mt-1 text-[11px] font-bold"
            style={{ color: BRAND_COLORS.red }}
            data-testid="out-of-time"
          >
            Час цього відрізка вийшов.{' '}
            {breakMinutes > 0
              ? `Відпочинь ${minutesLabel(breakMinutes)} — і почнемо нову сесію.`
              : 'Повертайся трохи пізніше.'}
          </p>
        ) : minutesToBreak !== null ? (
          <p className="mt-1 text-[11px] font-bold" style={{ color: BRAND_COLORS.cyan }}>
            До перерви {minutesToBreak} хв — сесія підлаштується під цей час.
          </p>
        ) : null}
      </Panel>

      {start.isError && !breakResumeAt(start.error) ? (
        <ErrorState
          error={start.error}
          title="Не вдалося почати"
          onRetry={() => start.mutate()}
        />
      ) : null}

      <Button
        tone="gold"
        size="xl"
        block
        loading={start.isPending}
        disabled={start.isPending || !learnerId || outOfTime}
        onClick={() => start.mutate()}
        data-testid="start-session"
      >
        {outOfTime
          ? 'Час вийшов — час відпочити'
          : start.isPending
            ? 'Готуємо арену…'
            : `У бій · ${DIFFICULTY_MODE_SPECS[mode].title}`}
      </Button>

      <p className="text-center text-[11px] font-bold text-white/40">
        Усі питання завантажаться одразу — якщо інтернет зникне, ти спокійно дограєш сесію.
      </p>
    </main>
  );
}
